import { useState } from 'react';
import {
  alpha,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccessibleModules } from '@core/router/useAccessibleModules';
import { ModuleTiles } from './ModuleTiles';
import type { ModuleTile } from './ModuleTiles';

interface CurrentModuleHeaderProps {
  /** Called after navigating to another module (closes the mobile drawer) */
  onModuleChange?: () => void;
}

/**
 * CurrentModuleHeader Component
 *
 * Top row of the sidebar: the current module name, which is also the module
 * switcher. Clicking it opens the module tiles - a popover under the row on
 * desktop, a centered dialog on mobile. With nothing to switch to it is a
 * plain heading.
 */
export const CurrentModuleHeader = ({ onModuleChange }: CurrentModuleHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { accessibleModules, currentModule } = useAccessibleModules();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!currentModule) {
    return null;
  }

  const tiles: ModuleTile[] = accessibleModules.map((module) => ({
    id: module.id,
    label: t(module.labelKey),
    icon: module.icon,
    isCurrent: module.id === currentModule.id,
    isExternal: false,
    onOpen: () => navigate(module.basePath),
  }));

  const canSwitch = tiles.length > 1;
  const moduleName = t(currentModule.labelKey);

  const close = () => setAnchorEl(null);

  const handleSelect = (tile: ModuleTile) => {
    close();
    // Picking the module you are already in only closes the switcher
    if (tile.isCurrent) return;
    tile.onOpen();
    if (!tile.isExternal) onModuleChange?.();
  };

  const title = (
    <Typography
      component="span"
      noWrap
      sx={{
        flexGrow: 1,
        textAlign: 'left',
        fontSize: '0.8125rem',
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {moduleName}
    </Typography>
  );

  const rowSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.75,
    color: 'primary.main',
  } as const;

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
      }}
    >
      {canSwitch ? (
        <Tooltip title={isMobile ? '' : t('app.moduleSwitcher.change')} placement="right">
          <ButtonBase
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label={t('app.moduleSwitcher.changeCurrent', { name: moduleName })}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{
              ...rowSx,
              bgcolor: open ? (th) => alpha(th.palette.primary.main, 0.08) : undefined,
              '&:hover': {
                bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
              },
            }}
          >
            {title}
            <UnfoldMoreIcon sx={{ fontSize: '1.125rem' }} />
          </ButtonBase>
        </Tooltip>
      ) : (
        <Box sx={rowSx}>{title}</Box>
      )}

      {/* Desktop: popover right under the row */}
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { sx: { mt: 0.5, ml: 1, p: 1.5, width: 320 } },
          }}
        >
          <ModuleTiles tiles={tiles} onSelect={handleSelect} />
        </Popover>
      )}

      {/* Mobile: centered dialog, big touch targets */}
      {isMobile && (
        <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
          <DialogTitle sx={{ pb: 1, fontSize: '1rem', fontWeight: 600 }}>
            {t('app.moduleSwitcher.title')}
          </DialogTitle>
          <DialogContent>
            <ModuleTiles tiles={tiles} onSelect={handleSelect} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
